import React, {useState, useEffect, useContext, useMemo} from 'react'
import {Link} from 'react-router-dom'
import {LangContext} from '../../context/lang-context'
import useFetch from '../../hooks/fetchMSSQL-hook'
import NoResults from '../../components/NoResults/NoResults'
import moment from 'moment'
import {NavigationContext} from '../../context/navigation-context'
import SearchFilter from '../../components/Filter/SearchFilter'

const KOS = () => {
  const {TRL_Pack} = useContext(LangContext);
  const {fetchMssqlApi} = useFetch();
  const {setHeaderData} = useContext(NavigationContext);

  const [KOS, setKOS] = useState([]);

  const defaultAdditionalFilter = {ko: "", date: "", machine: "", do: "", ro: "", correction: "", comment: "", wi: ""};
  const [additionalFilter, setAdditionalFilter] = useState(defaultAdditionalFilter);

  const defaultFilter = {
    dateTimeRangeFrom: moment().startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
    dateTimeRangeTo: moment().endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
    name: 'KOS',
    page: 1,
    rowsPerPage: 50,
    machineId: null,
    machineName: '',
    userId: null,
    userName: '',
    machineTags: {id: [], name: []},
    sortBy: 'none',
  };

  const [filter, setFilter] = useState(defaultFilter);
  const [settingsFilter, setSettingsFilter] = useState({
    saveFilters: true,
    autoLoading: true,
    additionalFilter: true,
    flatView: false,
    rowsPerPage: 50,
  });

  const rowsPerPage = settingsFilter.rowsPerPage || filter.rowsPerPage || 50;

  const sortDefaults = {
    KO: 'ASC',
    CreateDateTime: 'DESC',
    MachineFullName: 'ASC',
    DOCount: 'DESC',
    ROCount: 'DESC',
    CorrectionName: 'ASC',
    Comment: 'ASC',
    WI: 'ASC',
  };

  const getSortState = () => {
    const [field, dir] = String(filter.sortBy || '').split('|');
    return {field, dir: (dir || '').toUpperCase()};
  };

  const sortState = getSortState();

  const arrow = (field) => {
    if (sortState.field !== field) return '';
    return sortState.dir === 'ASC' ? ' ▲' : ' ▼';
  };

  const handleHeaderSort = (field) => {
    const [curField, curDirRaw] = String(filter.sortBy || '').split('|');
    const curDir = (curDirRaw || '').toUpperCase();

    let nextDir;
    if (curField === field) nextDir = curDir === 'ASC' ? 'DESC' : 'ASC';
    else nextDir = sortDefaults[field] || 'ASC';

    const next = {...filter, sortBy: `${field}|${nextDir}`, page: 1};
    setFilter(next);
    getKOS(next);
  };

  const getMachineIdsByTags = (machineTagIds) => {
    return new Promise((resolve, reject) => {
      fetchMssqlApi(
        "tags/getMachineIdsByTags",
        {method: "POST", hideNotification: true, data: {machineTagIds}},
        (response) => {resolve(JSON.stringify(response.map(Number).filter(Number.isFinite)) || [])},
        (err) => reject(err)
      );
    });
  };

  const getKOS = async (f) => {
    let machineIds = "[]";
    if (f?.machineTags?.id?.length > 0) machineIds = await getMachineIdsByTags(f.machineTags.id);
    if (f?.machineId) {const arr = JSON.parse(machineIds); arr.push(Number(f.machineId)); machineIds = JSON.stringify(arr);}

    fetchMssqlApi(
      'kos',
      {
        method: 'POST',
        hideNotification: true,
        data: {
          fromCreateDateTime: f?.dateTimeRangeFrom,
          toCreateDateTime: f?.dateTimeRangeTo,
          machineIds,
          createUserId: f?.userId,
          sortBy: f?.sortBy,
        },
      },
      (response) => setKOS(response)
    );
  };

  useEffect(() => {
    setHeaderData({text: TRL_Pack.ko.kos});
  }, []);

  const filtered = useMemo(() => {
    return KOS?.filter(item =>
      (additionalFilter.ko === '' || `${item.KO || ''}`.toLowerCase().includes(additionalFilter.ko.toLowerCase())) &&
      (additionalFilter.date === '' || `${item.Created || ''}`.toLowerCase().includes(additionalFilter.date.toLowerCase())) &&
      (additionalFilter.machine === '' || `${item.MachineFullName || ''}`.toLowerCase().includes(additionalFilter.machine.toLowerCase())) &&
      (additionalFilter.do === '' || `${item.DO || ''} ${item.DOCount || ''}`.toLowerCase().includes(additionalFilter.do.toLowerCase())) &&
      (additionalFilter.ro === '' || `${item.RO || ''} ${item.ROCount || ''}`.toLowerCase().includes(additionalFilter.ro.toLowerCase())) &&
      (additionalFilter.correction === '' || `${item.CorrectionName || ''}`.toLowerCase().includes(additionalFilter.correction.toLowerCase())) &&
      (additionalFilter.comment === '' || `${item.Comment || ''}`.toLowerCase().includes(additionalFilter.comment.toLowerCase())) &&
      (additionalFilter.wi === '' || `${item.WI || ''}`.toLowerCase().includes(additionalFilter.wi.toLowerCase()))
    ) ?? [];
  }, [KOS, additionalFilter]);

  const inputStyle = (value) => ({
    backgroundColor: value ? "#d1d5db" : "#f8f9fa",
    border: value ? "2px solid #634b4b" : "1px solid #ccc",
    boxShadow: value ? "0 0 0 2px rgba(75,85,99,.15)" : "none",
    fontWeight: value ? "600" : "normal",
    color: value ? "#111827" : "inherit",
    width: "100%",
    textAlign: "center",
    padding: "2px 4px",
    fontSize: "10px",
    height: "22px",
    borderRadius: "3px"
  });

  return (
    <div className="container-fluid p-0">
      <SearchFilter
        filterName="KOS"
        defaultFilter={defaultFilter}
        onFilterChange={(f) => setFilter(f)}
        onSettingsChange={(s) => setSettingsFilter(s)}
        search={getKOS}
        totalCount={filtered.length}
        defaultAdditionalFilter={defaultAdditionalFilter}
        additionalFilter={additionalFilter}
        onAdditionalFilterChange={(af) => setAdditionalFilter(af)}
        forNow
      />

      <div className="container-fluid">
        <div className="border overflow-auto custom-scroll" style={{overflowY: "auto"}}>
          {KOS.length ? (
            <table className="table table-striped">
              <colgroup>
                <col style={{minWidth: "125px", width: "125px"}}/>
                <col style={{minWidth: "180px", width: "180px"}}/>
                <col style={{minWidth: "220px"}}/>
                <col style={{minWidth: "145px", width: "145px"}}/>
                <col style={{minWidth: "145px", width: "145px"}}/>
                <col style={{minWidth: "180px"}}/>
                <col style={{minWidth: "200px"}}/>
                <col style={{minWidth: "125px", width: "125px"}}/>
              </colgroup>

              <thead>
                <tr className="text-center headers-color">

                  <td>
                    <div className="d-flex flex-column align-items-center">
                      <span style={{cursor: "pointer", userSelect: "none"}} onClick={() => handleHeaderSort("KO")}>Korekta{arrow("KO")}</span>
                      <input type="text" value={additionalFilter.ko || ""} onChange={({target:{value}}) => setAdditionalFilter(prev => ({...prev, ko: value}))} style={inputStyle(additionalFilter.ko)}/>
                    </div>
                  </td>

                  <td>
                    <div className="d-flex flex-column align-items-center">
                      <span style={{cursor: "pointer", userSelect: "none"}} onClick={() => handleHeaderSort("CreateDateTime")}>Czas zapisu{arrow("CreateDateTime")}</span>
                      <input type="text" value={additionalFilter.date || ""} onChange={({target:{value}}) => setAdditionalFilter(prev => ({...prev, date: value}))} style={inputStyle(additionalFilter.date)}/>
                    </div>
                  </td>

                  <td>
                    <div className="d-flex flex-column align-items-center">
                      <span style={{cursor: "pointer", userSelect: "none"}} onClick={() => handleHeaderSort("MachineFullName")}>{TRL_Pack.wi.MachineFullName}{arrow("MachineFullName")}</span>
                      <input type="text" value={additionalFilter.machine || ""} onChange={({target:{value}}) => setAdditionalFilter(prev => ({...prev, machine: value}))} style={inputStyle(additionalFilter.machine)}/>
                    </div>
                  </td>

                  <td>
                    <div className="d-flex flex-column align-items-center">
                      <span style={{cursor: "pointer", userSelect: "none"}} onClick={() => handleHeaderSort("DOCount")}>Doładowanie{arrow("DOCount")}</span>
                      <input type="text" value={additionalFilter.do || ""} onChange={({target:{value}}) => setAdditionalFilter(prev => ({...prev, do: value}))} style={inputStyle(additionalFilter.do)}/>
                    </div>
                  </td>

                  <td>
                    <div className="d-flex flex-column align-items-center">
                      <span style={{cursor: "pointer", userSelect: "none"}} onClick={() => handleHeaderSort("ROCount")}>Rozładowanie{arrow("ROCount")}</span>
                      <input type="text" value={additionalFilter.ro || ""} onChange={({target:{value}}) => setAdditionalFilter(prev => ({...prev, ro: value}))} style={inputStyle(additionalFilter.ro)}/>
                    </div>
                  </td>

                  <td>
                    <div className="d-flex flex-column align-items-center">
                      <span style={{cursor: "pointer", userSelect: "none"}} onClick={() => handleHeaderSort("CorrectionName")}>Powód korekty{arrow("CorrectionName")}</span>
                      <input type="text" value={additionalFilter.correction || ""} onChange={({target:{value}}) => setAdditionalFilter(prev => ({...prev, correction: value}))} style={inputStyle(additionalFilter.correction)}/>
                    </div>
                  </td>

                  <td>
                    <div className="d-flex flex-column align-items-center">
                      <span style={{cursor: "pointer", userSelect: "none"}} onClick={() => handleHeaderSort("Comment")}>Komentarz{arrow("Comment")}</span>
                      <input type="text" value={additionalFilter.comment || ""} onChange={({target:{value}}) => setAdditionalFilter(prev => ({...prev, comment: value}))} style={inputStyle(additionalFilter.comment)}/>
                    </div>
                  </td>

                  <td>
                    <div className="d-flex flex-column align-items-center">
                      <span style={{cursor: "pointer", userSelect: "none"}} onClick={() => handleHeaderSort("WI")}>Wizyta{arrow("WI")}</span>
                      <input type="text" value={additionalFilter.wi || ""} onChange={({target:{value}}) => setAdditionalFilter(prev => ({...prev, wi: value}))} style={inputStyle(additionalFilter.wi)}/>
                    </div>
                  </td>

                </tr>
              </thead>

              <tbody>
                {filtered.slice(
                  (filter.page - 1) * rowsPerPage,
                  filter.page * rowsPerPage
                ).map((entry, idx) => (
                  <tr className="text-center" key={idx}>

                    <td className="px-1 py-0">
                      <div className="d-flex justify-content-center align-items-center">
                        {entry.KO !== '' && (
                          <Link to={`/ko/${entry.CorrectionId}`} className="btn text-nowrap btn-sm btn-outline-secondary">
                            {entry.KO}
                          </Link>
                        )}
                      </div>
                    </td>

                    <td className="px-1 py-0">
                      <div className="d-flex justify-content-center align-items-center">
                        {entry.Created}
                      </div>
                    </td>

                    <td className="px-1 py-0">
                      <div className="d-flex justify-content-center align-items-center">
                        <Link to={`/machine/${entry.MachineId}`} className="btn btn-sm btn-outline-link">
                          {entry.MachineFullName}
                        </Link>
                      </div>
                    </td>

                    <td className="px-1 py-0">
                      <div className="d-flex flex-column justify-content-center align-items-center">
                        {entry.LoadMachineInventoryId && (
                          <>
                            <Link to={`/do/${entry.LoadMachineInventoryId}`} className="btn text-nowrap btn-sm btn-outline-info">
                              {entry.DO}
                            </Link>
                            <span>{entry.DOCount} szt.</span>
                          </>
                        )}
                      </div>
                    </td>

                    <td className="px-1 py-0">
                      <div className="d-flex flex-column justify-content-center align-items-center">
                        {entry.UnloadMachineInventoryId && (
                          <>
                            <Link to={`/ro/${entry.UnloadMachineInventoryId}`} className="btn text-nowrap btn-sm btn-outline-danger">
                              {entry.RO}
                            </Link>
                            <span>{entry.ROCount} szt.</span>
                          </>
                        )}
                      </div>
                    </td>

                    <td className="px-1 py-0">
                      <div className="d-flex justify-content-center align-items-center">
                        {entry.CorrectionName}
                      </div>
                    </td>

                    <td className="px-1 py-0">
                      <div className="d-flex justify-content-center align-items-center">
                        {entry.Comment}
                      </div>
                    </td>

                    <td className="px-1 py-0">
                      <div className="d-flex justify-content-center align-items-center">
                        <div className="btn text-nowrap btn-sm btn-outline-primary">
                          {entry.WI}
                        </div>
                      </div>
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <NoResults/>
          )}
        </div>
      </div>
    </div>
  );
};

export default KOS;